/*!
 * ymm-vin.js — offline motorcycle VIN decoder (port of HogPlay\Fitment\VinDecoder).
 * UMD pure core `YMMVinCore.decode(raw, vinData)` (Node-testable) + browser `window.YMMVin.decode(raw)`
 * which lazy-loads the VIN catalog via YMM.loadVin(). NO network in the algorithm, NO vPIC 1.66MB fallback:
 * deterministic header (WMI→make, pos10→year, mod-11 check digit, plant) + per-make model resolve.
 *
 * MAKE-AWARE, not Harley-only. It used to know only Harley WMIs, so an INDIAN VIN — on a store that sells
 * Indian fitment — was told "✗ Not a Harley-Davidson VIN", and so was a Honda. Routing now comes from the
 * data (`brands`, built from the Vin Combo fork's own brand_keys table), which matters because each make
 * keys its VDS off DIFFERENT VIN positions (Harley 5-7, Indian 4-7) — this cannot be hardcoded.
 *
 * Four honest outcomes, each a distinct `status`:
 *   ok               → a make we support (Harley/Indian), model resolved
 *   not_found        → a make we support, but its VDS isn't in the catalog → make+year only, pick the model
 *   unsupported_make → a real VIN we can identify (Honda, BMW, Victory…) but have no fitment for → say so
 *   unknown_wmi      → not a WMI we recognise at all
 *   invalid_length / illegal chars → not a VIN
 */
(function (root, factory) {
  var api = factory();
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (root) root.YMMVinCore = api;
  // browser augmentation: window.YMMVin.decode(raw) → Promise<envelope> (lazy data), snapped to the tree.
  if (root && typeof window !== 'undefined') {
    root.YMMVin = {
      decode: function (raw) {
        var YMM = root.YMM;
        if (!YMM || !YMM.loadVin) return Promise.reject(new Error('YMM core not loaded'));
        return YMM.loadVin().then(function (data) {
          var env = api.decode(raw, data);
          // snap decoded model to a tree model + build the canonical vehicle (fills ffam/vfam for resolution)
          if (env.supported && env.year) {
            // The factory catalogs prefix the platform ("Touring Street Glide"); the tree carries the bare
            // model ("Street Glide") because build-tree.mjs strips those. Without this, a VIN decode lands
            // on a model the tree doesn't know → wrong ffam and no landing page. Only strip when the raw
            // name misses AND the stripped one hits, so a real prefixed model is never mangled.
            var m = env.model;
            if (m && YMM.engine && !YMM.engine.findModel(env.make, m)) {
              // Strip VDS-catalog junk the tree doesn't carry, one token at a time, re-testing after each — so a
              // real model is NEVER mangled (only a stripped form the tree actually knows is adopted):
              //   • leading platform words ("Touring Street Glide" → "Street Glide"), doubled-up too
              //   • the "CVO/Custom " marketing prefix — keep the real "CVO" ("CVO/Custom CVO Road Glide" → "CVO Road Glide")
              //   • a leftover Sportster "Xl " ("Sportster Xl Iron 883" → "Iron 883")
              //   • a trailing engine-size token ("Street Bob 114" → "Street Bob"); only the HD CI sizes, so model-
              //     identity numbers (Street 500, Iron 883, X 350) are preserved.
              var STRIP = [/^(touring|softail|dyna|sportster|trike|vrsc)\s+/i, /^cvo\/custom\s+/i, /^xl\s+/i, /\s+(88|96|103|107|110|114|117|131)$/i];
              var g = m, changed = true, si, s2;
              while (g && !YMM.engine.findModel(env.make, g) && changed) {
                changed = false;
                for (si = 0; si < STRIP.length; si++) {
                  s2 = g.replace(STRIP[si], ' ').replace(/\s+/g, ' ').replace(/^\s+|\s+$/g, '');
                  if (s2 !== g) { g = s2; changed = true; break; }
                }
              }
              if (g !== m && YMM.engine.findModel(env.make, g)) { m = g; env.model = m; }
            }
            var pick = { make: env.make, model: m || null, year: env.year };
            env.vehicle = (YMM.engine ? YMM.engine.buildVehicle(pick) : null) ||
              { make: env.make, mk_name: env.make_name, model: env.model, year: env.year,
                ffam: null, rmodel: null, label: [env.year, env.make_name, env.model].filter(Boolean).join(' ') };
          }
          return env;
        });
      },
    };
  }
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';
  var VIN_CHARS = 'ABCDEFGHJKLMNPRSTUVWXYZ0123456789';
  var YEAR_LETTERS = 'ABCDEFGHJKLMNPRSTVWXY';
  // Fallback only — the real routing table ships in ymm-vin.json as `brands` (16 WMIs across 7 makes).
  // Used if an older/partial dataset is loaded, so a Harley VIN still decodes.
  var WMIS_FALLBACK = {
    '1HD': { make: 'harley-davidson', name: 'Harley-Davidson', pos: [5, 6, 7], supported: true },
    '5HD': { make: 'harley-davidson', name: 'Harley-Davidson', pos: [5, 6, 7], supported: true },
    '932': { make: 'harley-davidson', name: 'Harley-Davidson', pos: [5, 6, 7], supported: true },
    'HDR': { make: 'harley-davidson', name: 'Harley-Davidson', pos: [5, 6, 7], supported: true },
  };
  var MANUFACTURER = { '1HD': 'Harley-Davidson (USA)', '5HD': 'Harley-Davidson (USA)', '932': 'Harley-Davidson (Brazil)', 'HDR': 'Harley-Davidson' };
  var PLANTS = { Y: 'York, PA', A: 'York, PA', B: 'York, PA', C: 'York, PA', K: 'Kansas City, MO', J: 'Milwaukee, WI', T: 'Tomahawk, WI' };
  var TRANSLIT = { A:1,B:2,C:3,D:4,E:5,F:6,G:7,H:8,J:1,K:2,L:3,M:4,N:5,P:7,R:9,S:2,T:3,U:4,V:5,W:6,X:7,Y:8,Z:9 };
  var WEIGHTS = [8,7,6,5,4,3,2,10,0,9,8,7,6,5,4,3,2];

  function checkDigit(vin) {
    var total = 0;
    for (var i = 0; i < 17; i++) {
      var ch = vin[i];
      var v = /[0-9]/.test(ch) ? parseInt(ch, 10) : (TRANSLIT[ch] || 0);
      total += v * WEIGHTS[i];
    }
    var rem = total % 11;
    return rem === 10 ? 'X' : String(rem);
  }
  function yearCandidates(c, currentMy) {
    if (/[0-9]/.test(c)) { var d = parseInt(c, 10); return d === 0 ? [] : [2000 + d, 2030 + d]; }
    var i = YEAR_LETTERS.indexOf(c);
    return i < 0 ? [] : [1980 + i, 2010 + i];
  }
  function titlecase(s) {
    s = String(s).toLowerCase().replace(/\b\w/g, function (m) { return m.toUpperCase(); }).trim();
    return s.replace(/\b(cvo|st|us|ii|iii|abs)\b/gi, function (m) { return m.toUpperCase(); });
  }

  function decode(raw, data) {
    data = data || { catalog: [], overlays: [], current_my: 2027 };
    var CURRENT_MY = data.current_my || 2027;
    var brands = data.brands || WMIS_FALLBACK;
    var vin = String(raw || '').toUpperCase().replace(/[\s\-]+/g, '');
    var out = { vin: vin, warnings: [], valid_structure: true, is_harley: false, supported: false,
      make: null, make_name: null, model: null, series: null, family: null, year: null, status: 'ok' };

    if (vin.length !== 17) { out.valid_structure = false; out.status = 'invalid_length';
      out.warnings.push('length ' + vin.length + ' != 17'); return out; }
    var bad = [];
    for (var i = 0; i < 17; i++) if (VIN_CHARS.indexOf(vin[i]) < 0 && bad.indexOf(vin[i]) < 0) bad.push(vin[i]);
    if (bad.length) { out.valid_structure = false; out.warnings.push('illegal chars: ' + bad.join('')); }

    var wmi = vin.slice(0, 3);
    var brand = brands[wmi] || null;
    out.wmi = wmi;
    out.make = brand ? brand.make : null;
    out.make_name = brand ? brand.name : null;
    out.supported = !!(brand && brand.supported);
    out.is_harley = out.make === 'harley-davidson';        // kept for older callers
    out.manufacturer = MANUFACTURER[wmi] || (brand ? brand.name : null);
    var exp = checkDigit(vin);
    out.check_digit_valid = (exp === vin[8]);
    if (!out.check_digit_valid) out.warnings.push('check digit (pos 9) does not validate');
    out.plant = PLANTS[vin[10]] || null;

    // A VIN we can't place at all vs. one we can place but don't sell for — very different answers.
    if (!brand) { out.status = 'unknown_wmi'; out.warnings.push("WMI '" + wmi + "' is not a make we recognise"); return out; }
    if (!brand.supported) { out.status = 'unsupported_make'; return out; }

    var modelKey = vin.slice(4, 6);   // pos5-6
    var vds4 = vin.slice(3, 7);        // pos4-7
    var p57 = vin.slice(4, 7);         // pos5-7
    // The make's own VDS key, per its declared positions (Harley 5-7, Indian 4-7). 1-indexed in the table.
    var vdsKey = '';
    for (var pi = 0; pi < brand.pos.length; pi++) vdsKey += (vin[brand.pos[pi] - 1] || '');
    out.vds_key = vdsKey;
    var cands = yearCandidates(vin[9], CURRENT_MY).filter(function (y) { return y >= 1980 && y <= CURRENT_MY; });
    cands.sort(function (a, b) { return b - a; });   // recency first
    out.year_candidates = cands;

    // ---- Indian: (vds_key, model_year) → model, from the 398-row pattern table ----
    if (out.make === 'indian') {
      var ind = data.indian || [];
      for (var ic = 0; ic < cands.length; ic++) {
        for (var ir = 0; ir < ind.length; ir++) {
          if (ind[ir].y === cands[ic] && ind[ir].k === vdsKey) {
            out.year = cands[ic]; out.model = ind[ir].m; out.source = 'Indian VIN patterns';
            return out;
          }
        }
      }
      out.year = cands[0] || null; out.status = 'not_found'; out.source = 'unresolved';
      out.warnings.push("VDS '" + vdsKey + "' not in the Indian catalog — pick your model");
      return out;
    }

    // 1) overlays (usually empty) — match p57 or modelKey, newest candidate year in band
    var overlays = data.overlays || [];
    for (var o = 0; o < overlays.length; o++) {
      var ov = overlays[o];
      if (ov.vds === p57 || ov.vds === modelKey) {
        for (var yi = 0; yi < cands.length; yi++) {
          if (cands[yi] >= (ov.lo || 0) && cands[yi] <= (ov.hi || 9999)) {
            out.year = cands[yi]; out.model = ov.m; out.series = ov.s || null;
            out.source = 'overlay'; return out;
          }
        }
      }
    }
    // 2) HD factory catalog (authoritative): newest candidate year with a (year,vds4) hit, then (year,p57)
    var catalog = data.catalog || [];
    for (var c = 0; c < cands.length; c++) {
      var y = cands[c], hit = null, agnostic = false;
      for (var k = 0; k < catalog.length; k++) {
        if (catalog[k].y === y && catalog[k].v4 === vds4) { hit = catalog[k]; break; }
      }
      if (!hit) for (var k2 = 0; k2 < catalog.length; k2++) {
        if (catalog[k2].y === y && catalog[k2].v57 === p57) { hit = catalog[k2]; agnostic = true; break; }
      }
      if (hit) {
        out.year = y; out.model = titlecase(hit.m); out.series = hit.s || null; out.family = hit.f || null;
        out.source = agnostic ? 'HD catalog (pos4-agnostic)' : 'HD factory catalog';
        return out;
      }
    }
    // 3) no model — still return make + best-guess year
    out.year = cands[0] || null; out.status = 'not_found'; out.source = 'unresolved';
    out.warnings.push("VDS '" + modelKey + "' not in HD catalog — pick your model");
    return out;
  }

  return { decode: decode, checkDigit: checkDigit, yearCandidates: yearCandidates, titlecase: titlecase };
});
